import { deleteObject, getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { auth, storage } from "../../firebase";

function requireUid() {
  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error("You must be signed in.");
  return uid;
}

function imagePath(uid, deckId, cardId) {
  return `card_media/${uid}/${deckId}/${cardId}/image`;
}

function audioPath(uid, deckId, cardId) {
  return `card_media/${uid}/${deckId}/${cardId}/audio`;
}

function ensureImage(file) {
  if (!file) throw new Error("No image selected.");
  if (!file.type?.startsWith("image/")) throw new Error("Image must be an image file.");
  if (file.size > 8 * 1024 * 1024) throw new Error("Image is too large (max 8MB).");
}

function ensureAudio(file) {
  if (!file) throw new Error("No audio selected.");
  if (!file.type?.startsWith("audio/")) throw new Error("Audio must be an audio file.");
  if (file.size > 25 * 1024 * 1024) throw new Error("Audio is too large (max 25MB).");
}

export async function uploadCardImage({ deckId, cardId, file }) {
  const uid = requireUid();
  ensureImage(file);
  const path = imagePath(uid, deckId, cardId);
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, file, {
    contentType: file.type || "image/png",
    cacheControl: "public,max-age=3600",
  });
  const url = await getDownloadURL(storageRef);
  return { imageUrl: url, imagePath: path };
}

export async function uploadCardAudio({ deckId, cardId, file }) {
  const uid = requireUid();
  ensureAudio(file);
  const path = audioPath(uid, deckId, cardId);
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, file, {
    contentType: file.type || "audio/mpeg",
    cacheControl: "public,max-age=3600",
  });
  const url = await getDownloadURL(storageRef);
  return { audioUrl: url, audioPath: path };
}

export async function uploadGeneratedCardImageB64({ deckId, cardId, b64 }) {
  const uid = requireUid();
  const clean = String(b64 || "").trim();
  if (!clean) throw new Error("Generated image is empty.");

  const bin = atob(clean);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) {
    bytes[i] = bin.charCodeAt(i);
  }

  const blob = new Blob([bytes], { type: "image/png" });
  const path = imagePath(uid, deckId, cardId);
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, blob, {
    contentType: "image/png",
    cacheControl: "public,max-age=3600",
  });
  const url = await getDownloadURL(storageRef);
  return { imageUrl: url, imagePath: path };
}

export async function uploadGeneratedCardAudioB64({ deckId, cardId, b64 }) {
  const uid = requireUid();
  const clean = String(b64 || "").trim();
  if (!clean) throw new Error("Generated audio is empty.");

  const bin = atob(clean);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) {
    bytes[i] = bin.charCodeAt(i);
  }

  const blob = new Blob([bytes], { type: "audio/mpeg" });
  const path = audioPath(uid, deckId, cardId);
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, blob, {
    contentType: "audio/mpeg",
    cacheControl: "public,max-age=3600",
  });
  const url = await getDownloadURL(storageRef);
  return { audioUrl: url, audioPath: path };
}

export async function deleteStoragePath(path) {
  if (!path) return;
  try {
    await deleteObject(ref(storage, path));
  } catch (err) {
    if (err?.code !== "storage/object-not-found") throw err;
  }
}
